
import {
  defineComponent, onBeforeMount, reactive, watch, nextTick,
} from 'vue';
import message from 'ant-design-vue/lib/message';
import { useAppBase } from '@/core/composables/AppBase';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import ServicoInspecao from '@/servicos/ServicoInspecao';
import { EStatusResposta, IResposta } from '@/core/models/IResposta';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import Icone from '@/core/components/UI/Icone.vue';
import Card from '@/core/components/UI/Card.vue';
import { ICadastro } from '@/models/Entidades/ICadastro';
import storeApp from '@/store/storeApp';
import InspecaoValores from '@/components/InspecaoValores.vue';
import { IInspecaoValores } from '@/models/Entidades/IInspecaoValores';
import ComboSegurado from '@/components/ComboSegurado.vue';
import ComboCidade from '@/components/ComboCidade.vue';
import { IInspecao } from '@/models/Entidades/IInspecao';

export default defineComponent({
  name: 'InspecaoValoresCadastro',
  components: {
    ComunicacaoApi,
    InspecaoValores,
    Card,
    ComboSegurado,
    ComboCidade,
    Icone,
  },
  setup() {
    const {
      appBase, apresentarComunicacaoApi, apresentarResposta, apresentarMensagemSucesso, ocultarComunicacaoApi, obterCodigoRota,
    } = useAppBase();
    const servicoInspecao = new ServicoInspecao();

    const state = reactive({
      inspecao: {} as IInspecao,
      inspecaovalores: {} as IInspecaoValores,
      cep: '',
      apresentarCampos: false,
      apresentarMapa: false,
      apresentarCadastroPessoa: false,
      abaEnderecoLocalizacao: '1',
      bloquearCampos: false,
      gerarPdf: false,
      gerandoPdf: false,
      cnpjHistorico: '',
      consultandoCnpj: false,
      apresentarCadastroSegurado: false,
    });

    let debounce = 0;
    let monitorarAlteracoesInspecao = false;

    function marcarAlterado() {
      state.gerarPdf = false;
    }

    watch(
      () => state.inspecaovalores,
      () => {
        if (!monitorarAlteracoesInspecao) {
          return;
        }

        marcarAlterado();
      },
      {
        deep: true,
        flush: 'post',
      },
    );

    async function executarSemMonitorarAlteracoes<T>(
      acao: () => Promise<T> | T,
    ): Promise<T> {
      const monitoramentoAnterior = monitorarAlteracoesInspecao;

      monitorarAlteracoesInspecao = false;

      try {
        return await acao();
      } finally {
        // Aguarda o Vue processar todas as alterações pendentes.
        await nextTick();

        monitorarAlteracoesInspecao = monitoramentoAnterior;
      }
    }

    async function obterInspecao(codigo: number) {
      appBase.carregando = true;

      try {
        await executarSemMonitorarAlteracoes(async () => {
          state.inspecaovalores = await servicoInspecao.obterInspecaoValores(codigo);
        });
      } finally {
        appBase.carregando = false;
      }

      clearTimeout(debounce);

      debounce = setTimeout(() => {
        state.gerarPdf = true;
      }, 600);
    }

    async function obterInspecaoVinculada(codigoInspecao: number) {
      appBase.carregando = true;

      try {
        await executarSemMonitorarAlteracoes(async () => {
          state.inspecao = await servicoInspecao.obter(codigoInspecao);
          const inspecaoValores = await servicoInspecao.obterInspecaoValoresPorInspecao(codigoInspecao);
          if (UtilitarioGeral.objetoValido(inspecaoValores) && UtilitarioGeral.validaCodigo(inspecaoValores.codigo)) {
            state.inspecaovalores = inspecaoValores;
            if (!UtilitarioGeral.validaLista(state.inspecaovalores.arquivos)) {
              state.inspecaovalores.arquivos = [];
            }
          }
          state.inspecaovalores.codigoInspecao = state.inspecao.codigo;
          state.inspecaovalores.codigoCadastro = state.inspecao.codigoCadastro;
        });
      } finally {
        appBase.carregando = false;
      }

      if (state.inspecaovalores.codigo > 0) {
        clearTimeout(debounce);

        debounce = setTimeout(() => {
          state.gerarPdf = true;
        }, 600);
      }
    }

    async function limparDados() {
      state.apresentarCadastroSegurado = false;
      state.cnpjHistorico = '';
      state.consultandoCnpj = false;
      state.apresentarCampos = false;
      state.bloquearCampos = false;
      state.gerarPdf = false;
      state.gerandoPdf = false;
      state.apresentarCadastroPessoa = false;
      state.abaEnderecoLocalizacao = '1';
      state.inspecao = {} as IInspecao;
      state.inspecaovalores = {} as IInspecaoValores;
      state.inspecaovalores.codigo = 0;
      state.inspecaovalores.cadastro = {} as ICadastro;
      state.inspecaovalores.arquivos = [];
      state.cep = '';
      state.apresentarCampos = true;
    }

    onBeforeMount(async () => {
      await limparDados();

      const codigoInspecao = obterCodigoRota();
      console.log('codigo rota é', codigoInspecao);
      if (codigoInspecao > 0) {
        await obterInspecaoVinculada(codigoInspecao);
      }

      await nextTick();
      monitorarAlteracoesInspecao = true;
    });

    async function salvar() {
      appBase.resposta = {} as IResposta;
      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações da inspeção.');
      if (state.inspecaovalores.codigo === 0) {
        appBase.resposta = await servicoInspecao.incluirInspecaoValores(state.inspecaovalores);
      } else {
        appBase.resposta = await servicoInspecao.atualizarInspecaoValores(state.inspecaovalores);
      }
      if (appBase.resposta.status === EStatusResposta.Sucesso) {
        ocultarComunicacaoApi();
        if (state.inspecaovalores.codigo === 0) {
          apresentarMensagemSucesso('Valores da inspeção definidos com sucesso!');
          await obterInspecao(appBase.resposta.codigo);
        } else {
          apresentarMensagemSucesso('Valores da inspeção  atualizados com sucesso!');
        }
        state.gerarPdf = true;
      } else {
        apresentarResposta();
      }
    }

    async function gerarPdf() {
      state.gerandoPdf = true;
      const retornoRelatorio = await servicoInspecao.gerarPdfInspecaoValores(state.inspecaovalores.codigo);
      state.gerandoPdf = false;
      if (retornoRelatorio.status === EStatusResposta.Sucesso) {
        UtilitarioGeral.downloadArquivo(retornoRelatorio.link);
      } else {
        message.warn(retornoRelatorio.mensagem);
      }
    }

    function obtemEnderecoLocalRisco(): string {
      const partesEndereco = [state.inspecao.enderecoCompleto, state.inspecao.numero, state.inspecao.complemento, state.inspecao.bairro];
      return partesEndereco.filter((parte) => UtilitarioGeral.valorValido(parte)).join(', ');
    }

    return {
      storeApp,
      appBase,
      state,
      salvar,
      UtilitarioData,
      servicoInspecao,
      gerarPdf,
      marcarAlterado,
      obtemEnderecoLocalRisco,
    };
  },
});
